import {ChangeDetectionStrategy, Component, ElementRef, Inject, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {map, Observable, startWith} from "rxjs";
import {ContentService} from "../../_services/content.service";
import {SnackbarService} from "../../_services/snackbar.service";
import {AuthService} from "../../_services/auth.service";
import {Tag} from "../../_types/content";
import {MemoryWithManipulation} from "../../memory/memory.component";
import {GoogleLoaderService} from "../../_services/google-loader.service";
import PlaceResult = google.maps.places.PlaceResult;

interface MemoryFormData {
    timeline?: number;
    memory?: MemoryWithManipulation;
}

@Component({
    selector: 'app-memory-form',
    templateUrl: './memory-form.component.html',
    styleUrl: './memory-form.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class MemoryFormComponent {
    allowsLocation: boolean = false;
    memoryForm: FormGroup;
    loading: boolean = false;
    timelineId: number | undefined;
    memory: MemoryWithManipulation | undefined;
    tags: Tag[] = [];
    @ViewChild('tagInput') tagInput: any;
    filteredTags!: Observable<Tag[]>;
    selectedTags: number[] = [];
    tagSearchControl: FormControl = new FormControl();
    readonly minDate: Date = new Date("1800-01-01");
    readonly maxDate: Date = new Date();
    private autocomplete?: google.maps.places.Autocomplete;
    private pacContainer?: HTMLElement | null;

    constructor(protected dialogRef: MatDialogRef<MemoryFormComponent>,
                private formBuilder: FormBuilder, @Inject(MAT_DIALOG_DATA) private data: MemoryFormData,
                private contentService: ContentService,
                private snackbarService: SnackbarService,
                private googleLoader: GoogleLoaderService,
                private authService: AuthService) {

        this.maxDate.setFullYear(this.maxDate.getFullYear() + 100);

        this.authService.getDynamicSettings().then((r): void => {
            this.allowsLocation = r.location_features;
        });

        this.memoryForm = this.formBuilder.group({
            title: ['', Validators.required],
            description: [''],
            address: [''],
            attendees: [''],
            date: [''],
        });

        if (data?.memory) {
            this.memory = data.memory;
            if (this.memory) {
                this.memoryForm.patchValue({
                    title: this.memory.title,
                    description: this.memory.description,
                    date: this.memory.date,
                    attendees: this.memory.attendees,
                });
            }
        } else if (data?.timeline) {
            this.timelineId = data.timeline;
            this.memoryForm.patchValue({
                date: new Date(),
            });
        }
        this.contentService.getAllTags().subscribe({
            next: r => {
                this.tags = r.results;
                if (data?.memory?.tags) {
                    for (let tag of data?.memory?.tags) {
                        this.selectedTags.push(tag.id);
                    }
                }
            }, error: error => {
                console.error(error)
            }
        });

        this.filteredTags = this.tagSearchControl.valueChanges
            .pipe(
                startWith(''),
                map(value => this._filterTags(value))
            );
    }

    @ViewChild('placesInput') set placesInputContent(content: ElementRef) {
        if (content && this.allowsLocation) {
            this.initializeMap(content.nativeElement);
        }
    }

    async initializeMap(element: HTMLInputElement): Promise<void> {
        try {
            await this.googleLoader.loadGoogleMaps();
            this.initGoogleAutocomplete(element);
        } catch (error) {
            console.error("Maps failed to load", error);
        }
    }

    saveDetails(): void {
        this.loading = true;
        let date
        if (this.memoryForm.value.date) {
            date = new Date(this.memoryForm.value.date)
            date = date.toLocaleDateString('en-CA').slice(0, 10);
        }
        let body = {
            address: this.memoryForm.value.address,
            attendees: this.memoryForm.value.attendees,
            date: date,
            description: this.memoryForm.value.description,
            title: this.memoryForm.value.title,
        }
        if (this.memory) {
            this.contentService.patchMemory(this.memory.id, this.clean(body)).subscribe({
                next: r => {
                    this.loading = false;
                    this.dialogRef.close(r);
                }, error: error => {
                    this.loading = false;
                    this.snackbarService.openSnackBar(error, 'error')
                    console.error(error)
                }
            });
        } else if (this.timelineId) {
            this.contentService.createMemory(this.timelineId, this.clean(body)).subscribe({
                next: r => {
                    this.loading = false;
                    if (r.id) {
                        for (let tag of this.tags) {
                            if (this.selectedTags.includes(tag.id)) {
                                this.contentService.addTag(r.id, tag.name).subscribe({
                                    next: res => {
                                    }, error: error => {
                                        this.snackbarService.openSnackBar(error, 'error')
                                        console.error(error)
                                    }
                                });
                            }
                        }
                        this.dialogRef.close(r);

                    } else {
                        this.dialogRef.close(r);
                    }
                }, error: error => {
                    this.snackbarService.openSnackBar(error, 'error')
                    console.error(error)
                }
            });
        }
    }

    clean(obj: any) {
        // Remove empty keys
        for (var propName in obj) {
            if (obj[propName] === null || obj[propName] === undefined || obj[propName] === '') {
                delete obj[propName];
            }
        }
        return obj
    }

    selectTag(event: number): void {
        const tag: number = event;
        this.selectedTags.push(tag);
        if (this.tagInput && this.tagInput.nativeElement) {
            this.tagInput.nativeElement.value = '';
        }
        if (this.memory?.id) {
            for (let tag of this.tags) {
                if (tag.id == event) {
                    this.contentService.addTag(this.memory.id, tag.name).subscribe({
                        next: r => {
                            this.snackbarService.openSnackBar('Tag added successfully', 'success')

                        }, error: error => {
                            this.snackbarService.openSnackBar(error, 'error')
                            console.error(error)
                        }
                    });
                }
            }
        }
    }

    removeTag(tagId: any): void {
        const i: number = this.selectedTags.findIndex((x: number): boolean => x === tagId);
        if (i != null && i >= 0) {
            this.selectedTags.splice(i, 1);
        }
        if (this.memory?.id) {
            for (let tag of this.tags) {
                if (tag.id == tagId) {
                    this.contentService.deleteTag(this.memory.id, tag.name).subscribe({
                        next: r => {
                            this.snackbarService.openSnackBar('Tag removed successfully', 'success')

                        }, error: error => {
                            this.snackbarService.openSnackBar(error, 'error')
                            console.error(error)
                        }
                    });
                }
            }
        }
    }

    checkIfInArray(itemId: number, otherArray: number[]): boolean {
        let i
        if (otherArray) {
            i = otherArray.findIndex((x: number): boolean => x === itemId);
        }
        return (i != null && i >= 0);
    }

    private initGoogleAutocomplete(element: HTMLInputElement): void {
        // prevent double-init
        if (this.autocomplete) return;

        const options = {
            fields: ["address_components", "geometry", "formatted_address", "name"],
            strictBounds: false,
        };

        this.autocomplete = new google.maps.places.Autocomplete(element, options);

        if (this.memory?.address) element.value = this.memory.address;

        // Set ARIA combobox attributes for screen readers
        element.setAttribute('role', 'combobox');
        element.setAttribute('aria-autocomplete', 'list');
        element.setAttribute('aria-expanded', 'false');
        element.setAttribute('aria-haspopup', 'listbox');

        element.addEventListener('focus', (): void => {
            this.pacContainer = document.querySelector('.pac-container');
            const target: HTMLElement | null = document.getElementById('locationContainer');
            if (this.pacContainer && target && this.pacContainer.parentElement !== target) {
                target.appendChild(this.pacContainer);
            }
            if (this.pacContainer) {
                element.setAttribute('aria-expanded', 'true');
            }
        });

        element.addEventListener('blur', (): void => {
            element.setAttribute('aria-expanded', 'false');
        });

        this.autocomplete.addListener("place_changed", (): void => {
            const place: PlaceResult = this.autocomplete!.getPlace();
            this.memoryForm.patchValue({address: place.formatted_address});
            element.setAttribute('aria-expanded', 'false');
        });
    }

    private _filterTags(value: string): Tag[] {
        let filterValue: string = '';
        if (typeof value == 'string') {
            filterValue = value.toLowerCase();
        }
        let results: Tag[] = this.tags.filter((option: Tag): boolean => option.name.toLowerCase().includes(filterValue));
        results = results.filter((item: Tag, index: number): boolean => {
            return (results.indexOf(item) == index);
        });
        return results;
    }
}
